/* eslint-disable @typescript-eslint/no-use-before-define */
// Abstract base ↔ concrete subclass references below are intentional and safe
// under class hoisting — every reference site runs after both classes are fully
// initialized.
import { case as case_, union, void as voidType } from "@stellar/js-xdr";
import { XdrError, type XdrType } from "@stellar/js-xdr";
import { XdrValue } from "../values/xdr-value.js";
import { RevokeSponsorshipResultCode } from "./revoke-sponsorship-result-code.js";

export type RevokeSponsorshipResultWire =
  | { code: 0 }
  | { code: -1 }
  | { code: -2 }
  | { code: -3 }
  | { code: -4 }
  | { code: -5 };

export type RevokeSponsorshipResultVariantName =
  | "revokeSponsorshipSuccess"
  | "revokeSponsorshipDoesNotExist"
  | "revokeSponsorshipNotSponsor"
  | "revokeSponsorshipLowReserve"
  | "revokeSponsorshipOnlyTransferable"
  | "revokeSponsorshipMalformed";

/**
 * ```xdr
 * union RevokeSponsorshipResult switch (RevokeSponsorshipResultCode code)
 * {
 * case REVOKE_SPONSORSHIP_SUCCESS:
 *     void;
 * case REVOKE_SPONSORSHIP_DOES_NOT_EXIST:
 * case REVOKE_SPONSORSHIP_NOT_SPONSOR:
 * case REVOKE_SPONSORSHIP_LOW_RESERVE:
 * case REVOKE_SPONSORSHIP_ONLY_TRANSFERABLE:
 * case REVOKE_SPONSORSHIP_MALFORMED:
 *     void;
 * };
 * ```
 */
abstract class RevokeSponsorshipResultBase extends XdrValue {
  abstract readonly type: RevokeSponsorshipResultVariantName;

  constructor() {
    super();
    // `new.target`, not an unconditional throw: every arm subclass reaches
    // this constructor through `super()`, void arms via an implicit one
    if (new.target === RevokeSponsorshipResultBase) {
      throw new TypeError(
        "new xdr.RevokeSponsorshipResult(...) is not supported: XDR unions are built from " +
          "per-variant factories. Call xdr.RevokeSponsorshipResult.revokeSponsorshipSuccess() " +
          "(or another arm factory) instead.",
      );
    }
  }

  static readonly schema: XdrType<RevokeSponsorshipResultWire> = union(
    "RevokeSponsorshipResult",
    {
      switchOn: RevokeSponsorshipResultCode.schema,
      cases: [
        case_("revokeSponsorshipSuccess", 0, voidType()),
        case_("revokeSponsorshipDoesNotExist", -1, voidType()),
        case_("revokeSponsorshipNotSponsor", -2, voidType()),
        case_("revokeSponsorshipLowReserve", -3, voidType()),
        case_("revokeSponsorshipOnlyTransferable", -4, voidType()),
        case_("revokeSponsorshipMalformed", -5, voidType()),
      ],
      switchKey: "code",
    },
  );

  static revokeSponsorshipSuccess(): RevokeSponsorshipResultSuccess {
    return new RevokeSponsorshipResultSuccess();
  }

  static revokeSponsorshipDoesNotExist(): RevokeSponsorshipResultDoesNotExist {
    return new RevokeSponsorshipResultDoesNotExist();
  }

  static revokeSponsorshipNotSponsor(): RevokeSponsorshipResultNotSponsor {
    return new RevokeSponsorshipResultNotSponsor();
  }

  static revokeSponsorshipLowReserve(): RevokeSponsorshipResultLowReserve {
    return new RevokeSponsorshipResultLowReserve();
  }

  static revokeSponsorshipOnlyTransferable(): RevokeSponsorshipResultOnlyTransferable {
    return new RevokeSponsorshipResultOnlyTransferable();
  }

  static revokeSponsorshipMalformed(): RevokeSponsorshipResultMalformed {
    return new RevokeSponsorshipResultMalformed();
  }

  static fromXdrObject(
    wire: RevokeSponsorshipResultWire,
  ): RevokeSponsorshipResult {
    switch (wire.code) {
      case 0:
        return new RevokeSponsorshipResultSuccess();
      case -1:
        return new RevokeSponsorshipResultDoesNotExist();
      case -2:
        return new RevokeSponsorshipResultNotSponsor();
      case -3:
        return new RevokeSponsorshipResultLowReserve();
      case -4:
        return new RevokeSponsorshipResultOnlyTransferable();
      case -5:
        return new RevokeSponsorshipResultMalformed();
    }
    // unreachable for a well-typed wire object; a hand-built one can still
    // carry an out-of-range discriminant
    throw new XdrError(
      `RevokeSponsorshipResult: unknown code ${(wire as { code: unknown }).code}`,
    );
  }

  /**
   * Type guard narrowing an unknown value to a concrete RevokeSponsorshipResult variant.
   * Use this instead of `instanceof RevokeSponsorshipResult`: the exported `RevokeSponsorshipResult` value
   * is the abstract base, so `instanceof` narrows to the base (not the
   * variant union) and forces a cast. `RevokeSponsorshipResult.is(x)` narrows to the union.
   */
  static is(value: unknown): value is RevokeSponsorshipResult {
    return value instanceof RevokeSponsorshipResultBase;
  }

  abstract toXdrObject(): RevokeSponsorshipResultWire;
}

export class RevokeSponsorshipResultSuccess extends RevokeSponsorshipResultBase {
  readonly type = "revokeSponsorshipSuccess" as const;

  get value(): null {
    return null;
  }

  toXdrObject(): Extract<RevokeSponsorshipResultWire, { code: 0 }> {
    return { code: 0 };
  }
}

export class RevokeSponsorshipResultDoesNotExist extends RevokeSponsorshipResultBase {
  readonly type = "revokeSponsorshipDoesNotExist" as const;

  get value(): null {
    return null;
  }

  toXdrObject(): Extract<RevokeSponsorshipResultWire, { code: -1 }> {
    return { code: -1 };
  }
}

export class RevokeSponsorshipResultNotSponsor extends RevokeSponsorshipResultBase {
  readonly type = "revokeSponsorshipNotSponsor" as const;

  get value(): null {
    return null;
  }

  toXdrObject(): Extract<RevokeSponsorshipResultWire, { code: -2 }> {
    return { code: -2 };
  }
}

export class RevokeSponsorshipResultLowReserve extends RevokeSponsorshipResultBase {
  readonly type = "revokeSponsorshipLowReserve" as const;

  get value(): null {
    return null;
  }

  toXdrObject(): Extract<RevokeSponsorshipResultWire, { code: -3 }> {
    return { code: -3 };
  }
}

export class RevokeSponsorshipResultOnlyTransferable extends RevokeSponsorshipResultBase {
  readonly type = "revokeSponsorshipOnlyTransferable" as const;

  get value(): null {
    return null;
  }

  toXdrObject(): Extract<RevokeSponsorshipResultWire, { code: -4 }> {
    return { code: -4 };
  }
}

export class RevokeSponsorshipResultMalformed extends RevokeSponsorshipResultBase {
  readonly type = "revokeSponsorshipMalformed" as const;

  get value(): null {
    return null;
  }

  toXdrObject(): Extract<RevokeSponsorshipResultWire, { code: -5 }> {
    return { code: -5 };
  }
}

export type RevokeSponsorshipResult =
  | RevokeSponsorshipResultSuccess
  | RevokeSponsorshipResultDoesNotExist
  | RevokeSponsorshipResultNotSponsor
  | RevokeSponsorshipResultLowReserve
  | RevokeSponsorshipResultOnlyTransferable
  | RevokeSponsorshipResultMalformed;
export const RevokeSponsorshipResult = RevokeSponsorshipResultBase;
